import type { RefUser } from "Api/ref/User.types";

// Common types
export type Link = {
  href: string;
};

export type FormattedCost = {
  amount: number;
  currency: string;
  formatted: string;
};

export const CUSTOMER_TYPE = {
  individual: "individual",
  company: "company",
  government: "government"
} as const;

export type CustomerType = (typeof CUSTOMER_TYPE)[keyof typeof CUSTOMER_TYPE];

export type InvoiceType = "invoice" | "legacy_invoice";

export type InvoiceOwnerType = "billing_profile" | "organization";

export type BillingUserLevel = "admin" | "view";

export type BillingUserPermission = "manage" | "view";

// Billing Account types — billing_account_mapping
// Used by: GET /billing/accounts, POST /billing/accounts, GET /billing/accounts/{id},
//          GET /users/{billing_user_id}/billing/accounts
export type BillingAccountMapping = {
  id: string;
  owner_id: string;
  billing_email: string;
  salesforce_id: string;
  bp_account_id: string | null;
  country: string;
  name: string;
  company_name: string;
  legal_name: string;
  tax_number: string;
  tax_exempt: boolean;
  customer_type: CustomerType;
  billing_contacts: string;
  is_staff: boolean;
  created_at: string;
  updated_at: string;
};

// Base fields common to all customer types
type BillingAccountBaseFields = {
  country: string;
  name: string;
  customer_type: CustomerType;
};

// Individual customer - minimal requirements
type IndividualBillingAccount = BillingAccountBaseFields & {
  customer_type: "individual";
  legal_name?: string; // optional for individuals
  tax_number?: string;
  tax_exempt?: boolean;
  no_tax_number?: boolean;
};

// Corporation - requires legal info
type CorporationBillingAccount = BillingAccountBaseFields & {
  customer_type: "company";
  legal_name: string; // required for companies
  tax_number?: string;
  tax_exempt?: boolean;
  no_tax_number?: boolean;
};

// Government - similar to corporation
type GovernmentBillingAccount = BillingAccountBaseFields & {
  customer_type: "government";
  legal_name: string; // required for government entities
  tax_number?: string;
  tax_exempt?: boolean;
  no_tax_number?: boolean;
};

export type BillingAccountCreatableFields =
  | IndividualBillingAccount
  | CorporationBillingAccount
  | GovernmentBillingAccount;

// Fields a user can change themselves: the display name on any account, plus
// the one-way individual → company/government upgrade with its legal fields.
export type BillingAccountUpdatableFields = {
  name?: string;
  customer_type?: CustomerType;
  legal_name?: string;
  country?: string;
  tax_number?: string;
  tax_exempt?: boolean;
  no_tax_number?: boolean;
};

export type BillingAccountMappingWithLinks = BillingAccountMapping & {
  _links: {
    self: Link;
  };
};

// Profile Detail types (the detailed profile associated with a billing profile)
export type ProfileDetail = {
  billing_profile_id: string;
  billing_account_id: string;
  salesforce_id: string;
  name: string;
  billing_email: string;
  billing_contacts: string;
  country: string;
  currency: string;
  staff_account: boolean;
  pricing_model: "fixed" | "flex";
  billing_street_1: string;
  billing_street_2: string;
  administrative_area: string;
  locality: string;
  postal_code: string;
  account_tier: string;
  agency_partner_tier: string;
  created_at: string;
  updated_at: string;
  _links: {
    self: Link;
    "update-addons"?: Link;
    users?: Link;
    update?: Link;
  };
};

export type ProfileDetailCreatableFields = {
  name: string;
  billing_email?: string;
  country: string;
  rate_class?: string | null;
  pricing_model: "fixed" | "flex";
  billing_street_1: string;
  billing_street_2?: string;
  administrative_area: string;
  locality: string;
  postal_code: string;
};

// Invoice types
export type InvoiceLineItemProduct = {
  id: number;
  name: string;
  sku: string;
};

export type InvoiceLineItemPeriod = {
  start: string;
  end: string;
};

export type InvoiceDetail = {
  id: number;
  cost: FormattedCost;
  pricing: FormattedCost;
  tax: FormattedCost;
  total: FormattedCost;
  rated_amount: FormattedCost;
  discounted_by: number;
  estimated: boolean;
  prorate_factor: number;
  quantity: number;
  quantity_formatted: string;
  rate: number;
  // Unit price with its unit, e.g. "0,03 € / CPU-hr"; prefer this over
  // pricing.formatted, which omits the unit.
  unit_price_formatted: string;
  subscription: InvoiceLineItemPeriod;
  duration: InvoiceLineItemPeriod;
  product: InvoiceLineItemProduct;
  // Both are empty strings for billing-profile-level items (e.g. account-wide
  // discounts or support). Project-level items carry both; organization-level
  // items carry only organization_id.
  project_id: string;
  organization_id: string;
};

export type Invoice = {
  id: string;
  invoice_number: string;
  type: InvoiceType;
  status: string;
  owner: string;
  owner_type: InvoiceOwnerType;
  invoice_start_date: string | null;
  invoice_end_date: string | null;
  invoice_due: string | null;
  created: string | null;
  updated: string | null;
  payment_status: string;
  total: FormattedCost;
  _links: {
    self: Link;
    pdf?: Link;
    "invoice-details"?: Link;
    "invoice-payments"?: Link;
    "list-invoices"?: Link;
    "billing-profile"?: Link;
    "billing-dunning"?: Link;
  };
};

// Voucher types
export type VoucherInvoice = {
  invoice_id: string;
  status: string;
  billing_period_start: string;
  billing_period_end: string;
  invoice_total: string;
  invoice_discount: string;
  currency: string;
};

export type Voucher = {
  code: string;
  amount: string;
  currency: string;
  start_at: string;
  end_at: string;
  invoices: VoucherInvoice[];
};

export type VoucherList = {
  vouchers_total: string;
  vouchers_applied: string;
  vouchers_remaining_balance: string;
  currency: string;
  vouchers: Voucher[];
  _links: {
    self: {
      href: string;
    };
  };
};

// Payment Source types
export type PaymentSourceData = Record<string, unknown>;

export type PaymentSource = {
  type: string;
  type_label: string;
  payment_category: string;
  name: string;
  number: string;
  data: PaymentSourceData;
};

// Billing User types
export type BillingUser = {
  billing_profile_id: string;
  user_id: string;
  permissions: BillingUserPermission[];
  level: BillingUserLevel;
  created_at: string;
  updated_at: string;
  _links: {
    self?: {
      href: string;
    };
    update?: {
      href: string;
      method: string;
    };
    delete?: {
      href: string;
      method: string;
    };
  };
};

// API Response types. Collections paginate at 50 items via a `next` link;
// the request helpers follow it with fetchAllPages so consumers always get
// the complete list.
export type BillingProfileDetailResponse = {
  count: number;
  items: ProfileDetail[];
  _links?: {
    self?: Link;
    next?: Link;
  };
};

export type BillingAccountMappingsResponse = {
  items: BillingAccountMappingWithLinks[];
  _links?: {
    self?: Link;
    next?: Link;
  };
};

export type InvoicesResponse = {
  items: Invoice[];
  _links?: {
    self?: Link;
    next?: Link;
  };
};

export type InvoiceDetailsResponse = {
  items: InvoiceDetail[];
  _links: {
    self?: Link;
    // Zero or more links resolving to the organizations / projects referenced
    // by the invoice line items. Used to display names instead of raw IDs.
    "ref:organizations:0"?: { href: string };
    "ref:projects:0"?: { href: string };
  };
};

export type BillingUsersResponse = {
  items: BillingUser[];
  _links: {
    self?: Link;
    "ref:users:0"?: { href: string };
  };
};

export interface BillingUserData {
  billingUser: BillingUser;
  user?: RefUser;
}

export type PaymentSourcesResponse = {
  payment_source: PaymentSource;
  _links?: {
    self?: Link;
  };
};

export type PaymentIntentMessage = "sepa_info";

export type PaymentIntentResponse = {
  client_secret: string;
  public_key: string;
  messages: PaymentIntentMessage[];
  automatic_payment_methods: boolean;
};

export type AddPaymentSourcePayload = {
  type: string;
  token: string;
  email?: string;
  chargeable?: boolean;
};

export type AddPaymentSourceResponse = PaymentSource;

// Shared billing amount types
export type CurrencyAmount = {
  formatted: string;
  amount: number;
  currency_code: string;
  currency_symbol: string;
};

export type AmountBreakdown = {
  monthly: CurrencyAmount;
  commitment_period: CurrencyAmount | null;
  contract_total: CurrencyAmount | null;
};

// Discount types
export type DiscountStatus = "inactive" | "active" | "expired" | "deactivated";

export type DiscountType = "startup";

export type Discount = {
  id: number;
  billing_profile_id: string;
  type: DiscountType;
  type_label: string;
  status: DiscountStatus;
  discount: AmountBreakdown;
  config: Record<string, unknown>;
  start_at: string;
  end_at: string | null;
};

export type DiscountsResponse = {
  items: Discount[];
  _links: {
    self?: Link;
    next?: Link;
    previous?: Link;
  };
};

// Contract types
export type ContractType = "commitment";

export type ContractStatus = "inactive" | "active" | "expired" | "deactivated";

export type ContractSource = "billingplatform";

export type ContractFrequency =
  "monthly" | "quarterly" | "bi-annually" | "yearly";

export type Contract = {
  id: string;
  contract_number: string;
  status: ContractStatus;
  type: ContractType;
  source: ContractSource;
  start_at: string;
  end_at: string | null;
  billing_frequency: ContractFrequency;
  commitment_frequency: ContractFrequency;
  duration_months: string;
  discount_percent: string;
  bonus_percent: string;
  prepaid_amount: CurrencyAmount;
  commitment_amount: CurrencyAmount;
  credit_used: CurrencyAmount;
  credit_remaining: CurrencyAmount;
};

export type ContractsResponse = {
  contracts: Contract[];
  migrated_contracts: Contract[];
  _links: {
    self?: Link;
    next?: Link;
    previous?: Link;
  };
};

// Contract Usage types — for /billing/profiles/{id}/contract/{contract_id}/usage
export type ContractUsageInvoice = {
  invoice_id: string;
  status: string;
  billing_period_start: string;
  billing_period_end: string;
  invoice_total: string;
  invoice_discount: string;
  currency: string;
};

export type ContractUsageVoucher = {
  code: string;
  amount: string;
  applied: string;
  remaining: string;
  currency: string;
  start_at: string;
  end_at: string;
  invoices: ContractUsageInvoice[];
};

export type ContractUsage = {
  source: ContractSource;
  currency: string;
  credit_total: string;
  credit_applied: string;
  credit_remaining: string;
  vouchers: ContractUsageVoucher[];
  _links: {
    self: Link;
  };
};

export type AddonName = "support_level";
export type AddonOptionDetails = {
  title?: string;
  description?: string;
};
export type BillingProfileAddons = {
  available?: Record<AddonName, Record<string, number>>;
  current?: Record<AddonName, Record<string, number>>;
  details?: Record<AddonName, Record<string, AddonOptionDetails>>;
  upgrades_available?: Record<AddonName, string[]>;
};

export type BillingProfileAddonsUpdatePayload = Partial<
  Record<AddonName, string>
>;

// Organization Quote types
export type QuoteLineItem = {
  amount: number;
  currency_code: string;
  formatted: string;
  quantity: number;
  current_usage_formatted: string;
  label: string;
};

export type QuoteTotal = {
  amount: number;
  currency_code: string;
  formatted: string;
};

export type QuoteProject = {
  resources: QuoteLineItem[];
  resource_total: QuoteTotal;
  usage: QuoteLineItem[];
  usage_total: QuoteTotal;
  discounts: QuoteLineItem[];
  discount_total: QuoteTotal;
  plan: QuoteTotal;
  total: QuoteTotal;
};

export type QuoteOrganization = {
  addons: QuoteLineItem[];
  discounts: QuoteLineItem[];
};

export type OrganizationQuote = {
  projects: QuoteProject[];
  organization: QuoteOrganization;
  total: QuoteTotal;
};

// Organization Usage types — for /organizations/{id}/usage (new billing system)
export type OrganizationUsageProject = {
  subscription_id: string;
  environments: number;
};

export type OrganizationUsageTotals = {
  cpu: number;
  memory: number;
  projects: number;
  storage: number;
};

export type OrganizationUsage = {
  org: Record<string, unknown>;
  projects: OrganizationUsageProject[];
  totals: OrganizationUsageTotals;
};

// Project Current Usage types — for
// /billing/profiles/{id}/projects/{projectId}/current_usage (new billing system)
export type ProjectCurrentUsageItem = {
  product_name: string;
  current_usage: string;
  current_usage_formatted: string;
};

export type ProjectCurrentUsage = {
  items: ProjectCurrentUsageItem[];
  _links?: {
    self: { href: string };
  };
};

// Profile Estimation types — for /billing/profiles/{id}/estimates
export type EstimationLineItem = {
  amount: number;
  currency_code: string;
  formatted: string;
  quantity: number;
  current_usage_formatted: string;
  current_spend_formatted: string;
  unit_price_formatted: string;
  label: string;
};

export type EstimationMoney = {
  amount: number;
  currency_code: string;
  formatted: string;
};

export type EstimationOrganization = {
  organization_id: string;
  addons: EstimationLineItem[];
  discounts: EstimationLineItem[];
  total: EstimationMoney;
};

export type EstimationProject = {
  project_id: string;
  resources: EstimationLineItem[];
  resource_total: EstimationMoney;
  usage: EstimationLineItem[];
  usage_total: EstimationMoney;
  discounts: EstimationLineItem[];
  discount_total: EstimationMoney;
  plan: EstimationMoney;
  total: EstimationMoney;
};

export type ProfileEstimation = {
  addons: EstimationLineItem[];
  discounts: EstimationLineItem[];
  organizations: EstimationOrganization[];
  projects: EstimationProject[];
  total: EstimationMoney;
};

export type AlertMode = "projection" | "as_of";

// Per-project usage (traffic) alerts in the new billing system.
// GET /billing/profiles/{profileId}/alerts/projects/{projectId}/usage
export type ProjectUsageAlertType =
  | "egress_bandwidth"
  | "ingress_requests"
  | "fastly_bandwidth"
  | "fastly_requests";

export type ProjectUsageAlert = {
  type: ProjectUsageAlertType;
  active: boolean;
  alerts_sent: number;
  last_alert_at: string | null;
  updated_at: string;
  config: {
    threshold: {
      formatted: string;
      amount: number;
      // Unit comes from the endpoint (e.g. "GB", "M requests") and is shown
      // verbatim in the UI — the new billing system bills in GB, not TB.
      unit: string;
    };
    mode?: AlertMode;
  };
};

export type ProjectUsageAlertUpdate = {
  type: ProjectUsageAlertType;
  active: boolean;
  config: {
    threshold: number;
  };
};

export type ProfileAlert = {
  id: string;
  type?: string;
  active: boolean;
  alerts_sent: number;
  last_alert_at: string;
  updated_at: string;
  config: {
    threshold?: {
      formatted: string;
      amount: number;
      currency_code: string;
      currency_symbol: string;
    };
    mode?: AlertMode;
  };
};

// Hook function types
// Type for the patch function returned by useProfileAlert hook
// Returns the dispatch result from async thunks, which includes unwrap(), abort(), etc.
// Using Promise<unknown> to avoid coupling to Redux Toolkit's internal PayloadAction type
export type PatchAlertFunction = (threshold: number) =>
  | (Promise<unknown> & {
      abort: (reason?: string) => void;
      requestId: string;
      unwrap: () => Promise<unknown>;
    })
  | undefined;

// Type for the delete function returned by useProfileAlert hook
// Returns the dispatch result from async thunks, which includes unwrap(), abort(), etc.
export type DeleteAlertFunction = () =>
  | (Promise<unknown> & {
      abort: (reason?: string) => void;
      requestId: string;
      unwrap: () => Promise<unknown>;
    })
  | undefined;

export type BillingProfileStanding = {
  billing_profile_id: string;
  has_disputes: boolean;
  has_past_dues: boolean;
  manual_dunning: boolean;
};
